import { Component, inject, OnInit } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Articolo26, ModificaStatoIscrittoDto, MotivoCancellazione, Ordine, StatoIscritto } from '../../model/iscritti-data';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { IscrittiService } from '../../services/iscritti.service';

@Component({
  selector: 'app-dialog-cambio-stato',
  templateUrl: './dialog-cambio-stato.component.html',
  styleUrl: './dialog-cambio-stato.component.scss'
})
export class DialogCambioStatoComponent implements OnInit{
  private dialog_config = inject(DynamicDialogConfig);
  private dialog_ref = inject(DynamicDialogRef);
  private iscritti_service = inject(IscrittiService);

  isLoadingSpinner: boolean = false;

  //gestione stato iscritto
  stati: StatoIscritto[] = [
    { id: 1, nome: 'ATTIVO', codiceCnop: 'A' },
    { id: 2, nome: 'SOSPESO', codiceCnop: 'S' },
    { id: 3, nome: 'TRASFERIMENTO', codiceCnop: 'T' },
    { id: 4, nome: 'CANCELLATO', codiceCnop: 'C' },
    { id: 5, nome: 'RADIATO', codiceCnop: 'R' }
  ]

  //regioni
  ordiniData: Ordine[] = [];
  filteredOrdini: Ordine[] = [];
  motiviSospensione: Articolo26[] = [];
  motiviCancellazione: MotivoCancellazione[] = [];

  idIscritto: number | undefined;
  statoIscrittoAttuale: StatoIscritto | undefined;

  statoIscrittoForm = new FormGroup({
    statoIscritto: new FormControl<StatoIscritto | null>(null, Validators.required),
    dataStato: new FormControl<Date | null>(null, Validators.required),
    trasferimentoA: new FormControl<Ordine | null>(null, Validators.required),
    motivoSospensione: new FormControl<Articolo26|null>(null),
    motivoCancellazione: new FormControl<MotivoCancellazione|null>(null),
  })

  ngOnInit(): void {
    this.idIscritto = this.dialog_config.data.idIscritto;
    this.statoIscrittoAttuale = this.dialog_config.data.statoIscrittoAttuale;
    this.ordiniData = this.dialog_config.data.ordini;
    this.motiviSospensione = this.dialog_config.data.motiviSospensione;
    this.motiviCancellazione = this.dialog_config.data.motiviCancellazione;

    this.statoIscrittoForm.get('statoIscritto')?.patchValue(this.statoIscrittoAttuale!)
    this.statoIscrittoForm.get('statoIscritto')?.valueChanges.subscribe(() => this.changeSelectedStato())
  }

  //gestisce stringa data in base allo stato selezionato
  placeholderData(): string{
    let str = '';
    const selectedValue = this.statoIscrittoForm.get('statoIscritto')?.value?.nome;

    if(selectedValue === 'ATTIVO') str = 'Sospeso al';
    if(selectedValue === 'SOSPESO') str = 'Sospeso dal';
    if(['TRASFERIMENTO', 'CANCELLATO', 'RADIATO'].includes(selectedValue!)) str = 'In Data';

    return str;
  }

  //gestisce stato corrente
  gestoreStato(): boolean{
    const selectedValue = this.statoIscrittoForm.get('statoIscritto')?.value?.codiceCnop;
    const codiceAttuale = this.statoIscrittoAttuale?.codiceCnop;

    if((codiceAttuale === 'T' && selectedValue !== 'A') ||
      (codiceAttuale === 'S') ||
      (codiceAttuale === 'A' && ['S', 'C', 'R', 'T'].includes(selectedValue!)) ||
      (codiceAttuale === 'T' && ['S', 'C', 'R'].includes(selectedValue!))
    ) return true;

    return false;
  }

  //gestisce le date sul cambio di stato
  changeSelectedStato(){
    const selectedValue = this.statoIscrittoForm.get('statoIscritto')?.value?.codiceCnop;
    const codiceAttuale = this.statoIscrittoAttuale?.codiceCnop;

    this.validatorsHandler(selectedValue, codiceAttuale);
  }

  validatorsHandler(selectedValue: string | undefined, codiceAttuale: string | undefined){
    if(['A', 'S', 'C', 'R'].includes(selectedValue!)){
      this.statoIscrittoForm.get('statoIscritto')?.setValidators([Validators.required]);
      this.statoIscrittoForm.get('dataStato')?.setValidators([Validators.required]);
      this.statoIscrittoForm.get('trasferimentoA')?.clearValidators();
    }
    if(selectedValue === 'T'){
      this.statoIscrittoForm.get('statoIscritto')?.setValidators([Validators.required]);
      this.statoIscrittoForm.get('dataStato')?.setValidators([Validators.required]);
      this.statoIscrittoForm.get('trasferimentoA')?.setValidators([Validators.required]);
    }
    if(codiceAttuale === 'T' && selectedValue === 'A'){
      this.statoIscrittoForm.get('statoIscritto')?.setValidators([Validators.required]);
      this.statoIscrittoForm.get('dataStato')?.clearValidators();
      this.statoIscrittoForm.get('trasferimentoA')?.clearValidators();
    }
    this.statoIscrittoForm.get('statoIscritto')?.updateValueAndValidity({emitEvent: false});
    this.statoIscrittoForm.get('dataStato')?.updateValueAndValidity({emitEvent: false});
    this.statoIscrittoForm.get('trasferimentoA')?.updateValueAndValidity({emitEvent: false});
  }

  //invio dei dati in back
  inviaDati(){
    this.isLoadingSpinner = true;

    const valori = this.statoIscrittoForm.value;
    const data: ModificaStatoIscrittoDto = {
      stato: valori.statoIscritto!,
      data: valori.dataStato !== null ? this.formatDateToLocalDateString(valori.dataStato!) : null,
      trasferimentoA: valori.trasferimentoA!,
      motivoSospensione: valori.motivoSospensione ?? null,
      motivoCancellazione: valori.motivoCancellazione ?? null
    }

    this.iscritti_service.modificaStatoIscritto(this.idIscritto!, data).subscribe({
      next: () => {
        setTimeout(() => {
          this.dialog_ref.close({
            data
          })
        }, 1500);
      },
      error: (error) => {
        console.log(error)
      }
    });
  }

  formatDateToLocalDateString(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
